import React, { FC, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { Form as FormikForm, Formik } from 'formik';

import { Loaders, ValidatedSwitch, ValidatedTextField } from 'components';
import { PrivacyPolicySchema } from 'features/admin/validations';
import { AdminUpdateClientInput, useAdminUpdateClientMutation } from 'api';
import { useNotify, useProtectedClient } from 'hooks';
import { EMPTY_SLATE_EDITOR } from 'app-constants';
import SlateEditor from 'components/SlateEditor';

const styles = {
  submitContainer: {
    marginTop: '2em',
  },
  submitWrapper: {
    minWidth: 150,
  },
  switch: {
    position: 'relative',
    top: -1,
  },
} as const;

const Policies: FC = () => {
  const { t } = useTranslation();

  const notify = useNotify();
  const { id, social_media_policy_title, social_media_policy, social_media_policy_enabled } =
    useProtectedClient();

  const { mutate: adminUpdateClientMutation, isLoading } = useAdminUpdateClientMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      notify.info({ message: t('admin:socialMediaPolicySuccess') });
    },
  });

  function getInitialValue(): Partial<AdminUpdateClientInput> {
    return {
      id,
      social_media_policy: social_media_policy || EMPTY_SLATE_EDITOR,
      social_media_policy_title: social_media_policy_title || '',
      social_media_policy_enabled: social_media_policy_enabled || false,
    };
  }

  function handleKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  function handleSubmit(values: AdminUpdateClientInput) {
    adminUpdateClientMutation({ data: values });
  }

  function renderSubmit() {
    return (
      <Box sx={styles.submitWrapper}>
        <Button type="submit" size="large" variant="contained" color="primary" fullWidth>
          {isLoading ? <Loaders.Standard height={26} /> : t('common:save')}
        </Button>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={getInitialValue()}
      validationSchema={PrivacyPolicySchema}
      onSubmit={handleSubmit}
    >
      <FormikForm onKeyDown={handleKeyDown}>
        <Grid container>
          <Grid xs={3}>
            <Typography variant="subtitle2">{t('admin:socialMediaPolicy')}</Typography>
          </Grid>
          <Grid xs={7}>
            <Grid container direction="column">
              <Grid marginBottom={4}>
                <ValidatedTextField
                  removeLabel
                  name="social_media_policy_title"
                  i18nKey="admin:socialMediaPolicyTitle"
                  color="primary"
                  TextFieldProps={{ margin: 'none' }}
                />
              </Grid>
              <Grid>
                <SlateEditor
                  fieldName="social_media_policy"
                  label={t('admin:socialMediaPolicy')}
                  placeholder={t('admin:socialMediaPolicyPlaceholder')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container justifyContent="center" alignItems="flex-start" xs={2}>
            <ValidatedSwitch name="social_media_policy_enabled" color="primary" />
          </Grid>
        </Grid>
        <Grid container justifyContent="flex-end" sx={styles.submitContainer}>
          {renderSubmit()}
          <Grid xs={2} />
        </Grid>
      </FormikForm>
    </Formik>
  );
};

export default Policies;
